import {
  formatCurrency,
  PurchaseOrder,
  PurchaseOrderPayment,
  PurchaseOrderPaymentStatus,
  PurchaseStatus,
  Supplier,
  SupplierValuationPerSupplier,
} from '@bofrak-backend/shared';
import { apiAdapter } from '../../api/backend';
import {
  Badge,
  Box,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Grid,
  HStack,
  Progress,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { sortBy, reverse } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import { merchantAtom, storeAtom } from '../../recoil/atoms';
import { useSharedUiTranslation } from '../../../i18n';
import {
  getPurchasePaymentStatusLabel,
  getPurchaseStatusLabel,
} from '../../utils/status-labels';

interface SupplierDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  supplierId: string | null;
  supplier?: Supplier;
  /**
   * This supplier's row from the supplier valuation — the store-scoped,
   * receive-based source of truth. Passed down from the list that opened this
   * drawer rather than recomputed here, so the two can never disagree.
   */
  metrics?: SupplierValuationPerSupplier;
}

export function SupplierDrawer({
  isOpen,
  onClose,
  supplierId,
  supplier,
  metrics,
}: SupplierDrawerProps) {
  const { t } = useSharedUiTranslation();
  const merchant = useRecoilValue(merchantAtom);
  const store = useRecoilValue(storeAtom);

  const { data: allPurchaseOrders, isLoading: isLoadingPOs } = useQuery({
    queryKey: ['supplier-debts-pos', supplierId, merchant?.id],
    queryFn: () =>
      apiAdapter.getAllPurchaseOrders(merchant!.id, {
        by_supplier: true,
        supplier_id: supplierId!,
        merchant_id: merchant!.id,
      }),
    enabled: isOpen && !!supplierId && !!merchant?.id,
  });

  const { data: allPayments, isLoading: isLoadingPayments } = useQuery({
    queryKey: ['supplier-debts-payments', supplierId, merchant?.id],
    queryFn: () =>
      apiAdapter.getAllPurchaseOrderPayments(merchant!.id, {
        by_supplier: true,
        supplier_id: supplierId!,
      }),
    enabled: isOpen && !!supplierId && !!merchant?.id,
  });

  const purchaseOrders = useMemo(
    () =>
      reverse(
        sortBy(
          allPurchaseOrders?.purchase_orders.filter(
            (po: PurchaseOrder) => po.store_id === store?.id,
          ) ?? [],
          (po: PurchaseOrder) => new Date(po.created_at),
        ),
      ),
    [allPurchaseOrders, store?.id],
  );

  const payments = useMemo(
    () =>
      sortBy(
        allPayments?.purchase_order_payments.filter(
          (p: PurchaseOrderPayment) => p.store_id === store?.id,
        ) ?? [],
        'created_at',
      ),
    [allPayments, store?.id],
  );

  const summary: SupplierValuationPerSupplier = useMemo(
    () =>
      metrics ?? {
        count: 0,
        supplier_id: supplierId ?? '',
        supplier_due_start: 0,
        supplier_due_end: 0,
        total_cancelled_amount: 0,
        total_due: 0,
        total_paid: 0,
        total_money: 0,
        total_refunded: 0,
        final_amount: 0,
        paid_in_period: 0,
      },
    [metrics, supplierId],
  );

  const isLoading = isLoadingPOs || isLoadingPayments;
  const name = supplier?.name ?? supplierId ?? t('Supplier');

  return (
    <Drawer isOpen={isOpen} onClose={onClose} placement="right" size="lg">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader pb={1} px={3}>
          <Text fontSize="md" fontWeight="bold" noOfLines={1}>
            {name}
          </Text>
          {supplier?.phone_number && (
            <Text fontSize="xs" color="gray.500">
              {supplier.phone_number}
            </Text>
          )}
        </DrawerHeader>

        <DrawerBody px={3} pb={6}>
          {isLoading ? (
            <Flex justify="center" py={8}>
              <Spinner size="lg" color="blue.500" />
            </Flex>
          ) : (
            <VStack spacing={3} align="stretch">
              {/* Financial Summary Grid */}
              <Grid
                templateColumns="repeat(3, 1fr)"
                bg="blue.50"
                borderRadius="md"
                p={2}
                gap={1}>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="gray.500">
                    {t('Total')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    {formatCurrency(summary.total_money, true)}
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="gray.500">
                    {t('Cancelled')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    {formatCurrency(summary.total_cancelled_amount, true)}
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="gray.500">
                    {t('Final')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold">
                    {formatCurrency(summary.final_amount, true)}
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="blue.600">
                    {t('Paid')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" color="blue.600">
                    {formatCurrency(summary.total_paid, true)}
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="orange.500">
                    {t('Due')}
                  </Text>
                  <Text fontSize="xs" fontWeight="bold" color="orange.500">
                    {formatCurrency(summary.total_due, true)}
                  </Text>
                </Box>
                <Box textAlign="center">
                  <Text fontSize="2xs" color="red.500">
                    {t('Refunded')}
                  </Text>
                  <Text fontSize="xs" fontWeight="semibold" color="red.500">
                    {formatCurrency(summary.total_refunded, true)}
                  </Text>
                </Box>
              </Grid>

              {/* Tabs */}
              <Tabs isFitted size="sm" variant="line">
                <TabList>
                  <Tab fontSize="xs">
                    {t('{{count}} Order', { count: purchaseOrders.length })}
                  </Tab>
                  <Tab fontSize="xs">
                    {t('{{count}} Payment', { count: payments.length })}
                  </Tab>
                </TabList>
                <TabPanels>
                  <TabPanel px={0} py={2}>
                    <VStack spacing={2} align="stretch">
                      {purchaseOrders.map((po) => (
                        <ReadOnlyPOCard key={po.id} po={po} />
                      ))}
                      {purchaseOrders.length === 0 && (
                        <Text
                          fontSize="sm"
                          color="gray.400"
                          textAlign="center"
                          py={6}>
                          {t('No purchase orders')}
                        </Text>
                      )}
                    </VStack>
                  </TabPanel>
                  <TabPanel px={0} py={2}>
                    <VStack spacing={2} align="stretch">
                      {payments.map((p, i) => (
                        <PaymentCard key={i} payment={p} />
                      ))}
                      {payments.length === 0 && (
                        <Text
                          fontSize="sm"
                          color="gray.400"
                          textAlign="center"
                          py={6}>
                          {t('No payments yet')}
                        </Text>
                      )}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>
            </VStack>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/* ─── Read-Only PO Card ─── */

const STATUS_COLOR: Record<PurchaseStatus, string> = {
  [PurchaseStatus.DRAFT]: 'gray',
  [PurchaseStatus.CANCELLED]: 'red',
  [PurchaseStatus.PENDING]: 'blue',
  [PurchaseStatus.CLOSED]: 'green',
};

const PAYMENT_COLOR: Record<PurchaseOrderPaymentStatus, string> = {
  [PurchaseOrderPaymentStatus.PAID]: 'green',
  [PurchaseOrderPaymentStatus.PARTIALLY_PAID]: 'orange',
  [PurchaseOrderPaymentStatus.UNPAID]: 'red',
};

function ReadOnlyPOCard({ po }: { po: PurchaseOrder }) {
  const { t } = useSharedUiTranslation();
  const isCancelled = po.purchase_status === PurchaseStatus.CANCELLED;
  const receivedPct =
    po.total_ordered > 0
      ? Math.round((po.total_received / po.total_ordered) * 100)
      : 0;

  return (
    <Box
      borderWidth="1px"
      borderColor={isCancelled ? 'red.100' : 'gray.200'}
      borderRadius="md"
      px={3}
      py={2}
      bg={isCancelled ? 'red.50' : 'white'}
      opacity={isCancelled ? 0.7 : 1}>
      {/* Row 1: Date + Badges */}
      <Flex justify="space-between" align="center">
        <Text fontSize="2xs" color="gray.500">
          {moment(po.created_at).format('DD.MM.YY')}
        </Text>
        <HStack spacing={1}>
          <Badge
            fontSize="2xs"
            colorScheme={STATUS_COLOR[po.purchase_status]}
            variant="subtle">
            {getPurchaseStatusLabel(po.purchase_status)}
          </Badge>
          <Badge
            fontSize="2xs"
            colorScheme={PAYMENT_COLOR[po.payment_status]}
            variant="subtle">
            {getPurchasePaymentStatusLabel(po.payment_status)}
          </Badge>
        </HStack>
      </Flex>

      {/* Row 2: Amount + Due */}
      <Flex justify="space-between" align="center" mt={0.5}>
        <Text fontSize="xs" fontWeight="semibold">
          {formatCurrency(po.total_cost, true)}
        </Text>
        {po.po_amount_due != null && po.po_amount_due > 0 && (
          <Text fontSize="xs" fontWeight="bold" color="red.500">
            {t('Due {{amount}}', {
              amount: formatCurrency(po.po_amount_due, true),
            })}
          </Text>
        )}
      </Flex>

      {/* Row 3: Received progress */}
      {po.total_ordered > 0 && !isCancelled && (
        <Box mt={1}>
          <Flex justify="space-between" fontSize="2xs" color="gray.400">
            <Text>{t('Received')}</Text>
            <Text>
              {po.total_received.toFixed(0)}/{po.total_ordered.toFixed(0)}
            </Text>
          </Flex>
          <Progress
            value={receivedPct}
            colorScheme={
              receivedPct >= 70 ? 'green' : receivedPct >= 40 ? 'yellow' : 'red'
            }
            size="xs"
            borderRadius="full"
            mt={0.5}
          />
        </Box>
      )}
    </Box>
  );
}

/* ─── Payment Card ─── */

function PaymentCard({ payment }: { payment: PurchaseOrderPayment }) {
  const { t } = useSharedUiTranslation();
  const isPayment = payment.amount_paid > 0;

  return (
    <Box
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="md"
      px={3}
      py={2}
      bg="white">
      <Flex justify="space-between" align="center">
        <HStack spacing={2}>
          <Text fontSize="2xs" color="gray.500">
            {moment(payment.created_at).format('DD.MM.YY HH:mm')}
          </Text>
          <Badge
            fontSize="2xs"
            colorScheme={isPayment ? 'red' : 'green'}
            variant="subtle"
            borderRadius="sm">
            {isPayment ? t('Payment') : t('Refund')}
          </Badge>
        </HStack>
        <Text
          fontSize="xs"
          fontWeight="bold"
          color={isPayment ? 'red.600' : 'green.600'}>
          {isPayment ? '-' : ''}
          {formatCurrency(Math.abs(payment.amount_paid), true)}
        </Text>
      </Flex>

      <Flex justify="space-between" align="center" mt={0.5}>
        <Text fontSize="2xs" color="gray.600" noOfLines={1}>
          {t('PO {{id}}…', { id: payment.purchase_order_id.slice(0, 8) })}
        </Text>
        <Text fontSize="2xs" color="gray.500">
          {t('After:')}{' '}
          <Text as="span" fontWeight="semibold">
            {formatCurrency(payment.amount_due, true)}
          </Text>
        </Text>
      </Flex>

      {payment.creator_name && (
        <Text fontSize="2xs" color="gray.500" mt={0.5}>
          {t('By {{name}}', { name: payment.creator_name })}
        </Text>
      )}
    </Box>
  );
}
